import { PendingComponentSubAssembly } from "../types/component";
import {
  LandingGearType,
  LandingGearTypeCatalogItem,
  SubAssembly,
} from "../types/subAssembly";

export const LANDING_GEAR_TYPE_OPTIONS: LandingGearType[] = ["RMLG", "LMLG", "NLG"];

const SUB_ASSEMBLY_TYPES_BY_LANDING_GEAR: Record<
  LandingGearType,
  Record<string, string[]>
> = {
  RMLG: {
    Embraer: ["SHOCK STRUT", "SIDE STRUT", "2ND SIDE STRUT"],
    CRJ: ["SHOCK STRUT", "SIDESTAY", "RETRACTION ACTUATOR"],
  },
  LMLG: {
    Embraer: ["SHOCK STRUT", "SIDE STRUT", "2ND SIDE STRUT"],
    CRJ: ["SHOCK STRUT", "SIDESTAY", "RETRACTION ACTUATOR"],
  },
  NLG: {
    Embraer: ["SHOCK STRUT", "DRAG BRACE", "AUX DRAG"],
    CRJ: ["SHOCK STRUT", "DRAG BRACE"],
  },
};

const LEGACY_SUB_ASSEMBLY_TYPES_BY_LANDING_GEAR: Record<LandingGearType, string[]> = {
  RMLG: ["SHOCK STRUT", "SIDE STRUT", "2ND SIDE STRUT", "SIDESTAY", "RETRACTION ACTUATOR"],
  LMLG: ["SHOCK STRUT", "SIDE STRUT", "2ND SIDE STRUT", "SIDESTAY", "RETRACTION ACTUATOR"],
  NLG: ["SHOCK STRUT", "DRAG BRACE", "AUX DRAG"],
};

export function getSubAssemblyTypesForLandingGear(
  landingGearType: string,
  airframeFamily?: string
): string[] {
  const normalized = landingGearType.trim().toUpperCase() as LandingGearType;
  const family = airframeFamily?.trim();

  if (family && SUB_ASSEMBLY_TYPES_BY_LANDING_GEAR[normalized]?.[family]) {
    return SUB_ASSEMBLY_TYPES_BY_LANDING_GEAR[normalized][family];
  }

  return LEGACY_SUB_ASSEMBLY_TYPES_BY_LANDING_GEAR[normalized] ?? [];
}

const EMPTY_COMPONENT_ID = "00000000-0000-0000-0000-000000000000";

export function isAttachedSubAssembly(
  item: Pick<SubAssembly, "componentId" | "status"> | Partial<SubAssembly>
): boolean {
  const componentId = item.componentId;
  if (componentId && String(componentId) !== EMPTY_COMPONENT_ID) {
    return true;
  }

  const status = (item.status ?? "").trim().toLowerCase();
  return status === "attached" || status === "installed";
}

/** @deprecated Use isAttachedSubAssembly */
export const isSubAssemblyAttached = isAttachedSubAssembly;

export function getSubAssemblyOffWingType(
  item: Pick<SubAssembly, "status" | "offWingType" | "componentId">
): string | null {
  if (isAttachedSubAssembly(item)) {
    return null;
  }

  if (item.offWingType?.trim()) {
    return item.offWingType.trim();
  }

  const status = (item.status ?? "").trim();
  if (!status || status.toLowerCase() === "available") {
    return null;
  }

  return status;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

export function normalizeSubAssembly(raw: unknown): SubAssembly {
  const record = asRecord(raw) ?? {};
  const id = record.id ?? record.Id;
  const componentId = record.componentId ?? record.ComponentId;

  return {
    id: String(id ?? ""),
    landingGearType: String(
      record.landingGearType ?? record.LandingGearType ?? ""
    ) as LandingGearType,
    subAssemblyType: String(record.subAssemblyType ?? record.SubAssemblyType ?? ""),
    serialNumber: String(record.serialNumber ?? record.SerialNumber ?? ""),
    partNumber: (record.partNumber ?? record.PartNumber ?? null) as string | null,
    installDate: (record.installDate ?? record.InstallDate ?? null) as string | null,
    componentId:
      componentId &&
      String(componentId) !== EMPTY_COMPONENT_ID
        ? String(componentId)
        : null,
    status: String(record.status ?? record.Status ?? ""),
    offWingType: (record.offWingType ?? record.OffWingType ?? null) as string | null,
    shopVisitReasonId: (record.shopVisitReasonId ?? record.ShopVisitReasonId ?? null) as
      | string
      | null,
    shopVisitReasonLabel: (record.shopVisitReasonLabel ?? record.ShopVisitReasonLabel ?? null) as
      | string
      | null,
    createdDate: String(record.createdDate ?? record.CreatedDate ?? ""),
    modifiedDate: (record.modifiedDate ?? record.ModifiedDate ?? null) as string | null,
    createdBy: (record.createdBy ?? record.CreatedBy ?? null) as string | null,
    editedBy: (record.editedBy ?? record.EditedBy ?? null) as string | null,
  };
}

export function normalizeLandingGearType(raw: unknown): LandingGearTypeCatalogItem {
  const record = asRecord(raw) ?? {};
  const allowed =
    record.allowedSubAssemblyTypes ??
    record.AllowedSubAssemblyTypes ??
    record.subAssemblyTypes ??
    record.SubAssemblyTypes ??
    [];

  return {
    type: String(record.type ?? record.Type ?? record.code ?? record.Code ?? ""),
    label: String(record.label ?? record.Label ?? record.type ?? record.Type ?? ""),
    allowedSubAssemblyTypes: Array.isArray(allowed)
      ? allowed.map((item) => String(item))
      : [],
  };
}

export function subAssemblyLabel(item: Pick<SubAssembly, "serialNumber" | "partNumber">): string {
  if (item.partNumber?.trim()) {
    return `${item.serialNumber} (${item.partNumber})`;
  }
  return item.serialNumber;
}

export function sameSubAssemblyType(left: string, right: string): boolean {
  return left.trim().toUpperCase() === right.trim().toUpperCase();
}

export function normalizeLandingGearTypeCode(value: string): string {
  return value.trim().toUpperCase();
}

export function resolveLockedLandingGearType(
  items: Pick<PendingComponentSubAssembly, "landingGearType">[]
): string | null {
  const first = items.find((item) => item.landingGearType?.trim());
  return first ? normalizeLandingGearTypeCode(first.landingGearType) : null;
}

export type SubAssemblyAttachValidationResult =
  | { ok: true }
  | { ok: false; message: string };

export function validateSubAssemblyAttach(
  existing: Pick<
    PendingComponentSubAssembly,
    "id" | "landingGearType" | "subAssemblyType"
  >[],
  candidate: Pick<
    PendingComponentSubAssembly,
    "id" | "landingGearType" | "subAssemblyType"
  >
): SubAssemblyAttachValidationResult {
  if (existing.some((item) => item.id === candidate.id)) {
    return { ok: false, message: "This sub-assembly is already attached." };
  }

  const lockedType = resolveLockedLandingGearType(existing);
  const candidateType = normalizeLandingGearTypeCode(candidate.landingGearType);

  if (lockedType && candidateType !== lockedType) {
    return {
      ok: false,
      message: `Only ${lockedType} sub-assemblies can be attached. Remove existing sub-assemblies to switch landing gear type.`,
    };
  }

  if (
    existing.some((item) =>
      sameSubAssemblyType(item.subAssemblyType, candidate.subAssemblyType)
    )
  ) {
    return {
      ok: false,
      message: `A ${candidate.subAssemblyType} sub-assembly is already attached.`,
    };
  }

  return { ok: true };
}

export function formatSubAssemblyDisplayLabel(
  item: Pick<
    SubAssembly,
    "landingGearType" | "subAssemblyType" | "serialNumber" | "partNumber"
  >
): string {
  const partNumber = item.partNumber?.trim();
  const partSuffix = partNumber ? ` (PN: ${partNumber})` : "";
  return `${item.landingGearType} — ${item.subAssemblyType} — S/N: ${item.serialNumber}${partSuffix}`;
}
